import { Button } from '@/components/ui/button'
import BarChart from './chart/bar-chart'
import LineChart from './chart/line-chart'
import PieChart from './chart/pie-chart'
import StatsBarChart from './chart/stats-bar-chart'
import { Route as accountsOverviewRoute } from '@/routes/(pharma)/_layout/accounts/index'
import { convertToCurrencyRounded } from '@/utils/currency_converter'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import getPharmacyID from '@/utils/pharmacy_code'

const Component = () => {
    const { currentWeekCogs, lastWeekCogs, currentWeekRevenue, lastWeekRevenue, currentWeekPl, lastWeekPl, currentWeekPaymentMethodBreakdown, lastWeekPaymentMethodBreakdown } = accountsOverviewRoute.useLoaderData();
    const navigate = useNavigate();
    const mapped_cog = currentWeekCogs.cogsItems.map((item) => {
        return {
            day: dayjs(item.date?.toDate()).format('ddd'),
            "COG": item.totalCogs
        }
    })
    const this_week_cog_total = currentWeekCogs.cogsItems.reduce((acc, item) => acc + item.totalCogs, 0)
    const last_week_cog_total = lastWeekCogs.cogsItems.reduce((acc, item) => acc + item.totalCogs, 0)

    return (
        <>
            <div className="overflow-y-scroll h-screen pb-48">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">Accounts Overview</h2>
                        <p className="text-muted-foreground">
                            Click on any of the cards to get more detailed view with more filters.
                        </p>
                    </div>
                    {/* <Button
                        variant={"secondary"}
                    >
                        Customize
                    </Button> */}
                </div>
                <hr className="my-4" />
                <h2 className='text-3xl font-semibold mt-8 mb-4'>Revenue</h2>
                <div className='flex flex-col flex-1 gap-3'>
                    <StatsBarChart
                        title="Revenue (Weekly)"
                        value={convertToCurrencyRounded(currentWeekRevenue.revenueItems.reduce((acc, item) => acc + item.totalRevenue, 0))}
                        trend={convertToCurrencyRounded(lastWeekRevenue.revenueItems.reduce((acc, item) => acc + item.totalRevenue, 0)) + " vs Last Week"}
                        trendDirection={
                            lastWeekRevenue.revenueItems.reduce((acc, item) => acc + item.totalRevenue, 0) > currentWeekRevenue.revenueItems.reduce((acc, item) => acc + item.totalRevenue, 0) ? 'down' : 'up'
                        }
                        data={currentWeekRevenue.revenueItems.map((item) => {
                            return {
                                time_interval: dayjs(item.date?.toDate()).toISOString(),
                                total_revenue: item.totalRevenue
                            }
                        })}
                        xAxisKey="time_interval"
                        dataKey="total_revenue"
                        color="#71717A"
                        footer="Showing total revenue for the last 7 days"
                        onClick={() => {
                            navigate({
                                to: '/accounts/revenue',
                            })
                        }}
                        active={true}
                    />
                </div>

                <hr className="my-4" />

                <div className='grid grid-cols-3 gap-x-6'>
                    <BarChart
                        title="Cost of Goods Sold"
                        value={convertToCurrencyRounded(this_week_cog_total)}
                        trend= {`${convertToCurrencyRounded(Math.abs(this_week_cog_total-last_week_cog_total))} ${this_week_cog_total > last_week_cog_total ? 'less' : 'more'} vs Last Week`}
                        data={mapped_cog}
                        xAxisKey="day"
                        dataKey="COG"
                        color="#1458DD"
                        footer="Showing COGS for the last 7 days"
                        active={true}
                        onClick={()=>{
                            navigate({
                                to: '/accounts/cost-of-goods'
                            })
                        }}
                    />
                    <LineChart
                        title="Profit & Loss"
                        value={convertToCurrencyRounded(currentWeekPl.plItems.reduce((acc, item) => acc + item.totalProfitLoss, 0))}
                        trend={
                            (() => {
                                const thisWeek = currentWeekPl.plItems.reduce((acc, item) => acc + item.totalProfitLoss, 0);
                                const lastWeek = lastWeekPl.plItems.reduce((acc, item) => acc + item.totalProfitLoss, 0);
                                const diff = Math.abs(thisWeek - lastWeek);
                                let direction = '';
                                if (thisWeek > lastWeek) direction = 'more';
                                else if (thisWeek < lastWeek) direction = 'less';
                                return `${convertToCurrencyRounded(diff)} ${direction} vs Last Week`;
                            })()
                        }
                        data={currentWeekPl.plItems.map((item) => {
                            return {
                                time_interval: dayjs(item.date?.toDate()).format('ddd'),
                                "Profit/Loss": item.totalProfitLoss
                            }
                        })}
                        xAxisKey="time_interval"
                        dataKey="Profit/Loss"
                        color="#1458DD"
                        active={true}
                        footer='Showing Profit & Loss for the last 7 days'
                        onClick={() => {
                            navigate({
                                to: '/accounts/profit-loss'
                            })
                        }}
                    />
                    <PieChart 
                        title='Payment Method Breakdown'
                        value={convertToCurrencyRounded(currentWeekPaymentMethodBreakdown.paymentMethodBreakdownItems.reduce((acc, item) => acc + item.totalAmount, 0))}
                        trend={
                            (() => {
                                const thisWeek = currentWeekPaymentMethodBreakdown.paymentMethodBreakdownItems.reduce((acc, item) => acc + item.totalAmount, 0);
                                const lastWeek = lastWeekPaymentMethodBreakdown.paymentMethodBreakdownItems.reduce((acc, item) => acc + item.totalAmount, 0);
                                const diff = Math.abs(thisWeek - lastWeek);
                                let direction = '';
                                if (thisWeek > lastWeek) direction = 'more';
                                else if (thisWeek < lastWeek) direction = 'less';
                                return `${convertToCurrencyRounded(diff)} ${direction} vs Last Week`;
                            })()
                        }
                        data={currentWeekPaymentMethodBreakdown.paymentMethodBreakdownItems.map((item) => {
                            return {
                                name: item.paymentMethod,
                                value: item.totalAmount
                            }
                        })}
                        nameKey="name"
                        dataKey="value"
                        colors={['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']}
                        footer='Showing Payment Method Breakdown for the last 7 days'
                        valueType='amount'
                        onClick={() => {
                            navigate({
                                to: '/accounts/payment-method-breakdown'
                            })
                        }}
                        active={true}
                    />
                </div>
            </div>
        </>
    )
}

export default Component